import { createSelector, createSlice } from '@reduxjs/toolkit';
import { getTime } from 'date-fns';
import { find, get, includes, isEmpty, keyBy, map, remove, set } from 'lodash';
import { bookingConditionApi } from 'src/api/booking-condition-api';
import { bookingRequestApi } from 'src/api/booking-request-api';
import { settingApi } from 'src/api/setting-api';
import { configSettings } from 'src/config';
import { arrayRemoveValue } from 'src/utils/array-remove-value';
import { toTimestamp } from 'src/utils/parse';
import { bookingFindCarTabs, groupByOwned } from '../helper';

const initialState = {
  toastIds: [],
  map: {
    destinations: [
      { index: 0, lat: 10.823337255990584, lng: 106.62964221250725, address: 'Thành phố Hồ Chí Minh, Ho Chi Minh City, Vietnam' }
    ],
    directionsChanged: null
  },
  resultRouteMap: {
    isLoading: false,
    distanceText: '0 km',
    durationText: '',
    distance: null
  },

  resultConditions: {},
  resultRequests: {},

  openBookingCarResult: false,
  collapseCondition: false,
  tabSelected: null,
  tabNewNotices: [],
  tabConditions: null,
  settings: configSettings,
  ownedSelected: []
};

export const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    resetState: () => initialState,
    setMapDestinations: (state, action) => {
      state.map.destinations = action.payload;
      state.map.directionsChanged = null;
    },
    setResultRouteMap: (state, action) => {
      state.resultRouteMap = action.payload;
    },
    changeDirections: (state, action) => {
      state.map.directionsChanged = action.payload;
    },
    setTabSelected: (state, action) => {
      state.tabNewNotices = arrayRemoveValue(state.tabNewNotices, action.payload.id);
      state.tabSelected = action.payload;
    },
    updateTabSelectedCondition: (state, action) => {
      const tabId = get(state.tabSelected, 'id');

      state.tabConditions[tabId] = { ...action.payload, id: tabId };
    },
    updateTabCondition: (state, action) => {
      const tabId = get(action.payload, 'id');

      const tabCondition = get(state.tabConditions, tabId);
      if (
        tabCondition &&
        (toTimestamp(tabCondition?.start) !== action.payload?.start || toTimestamp(tabCondition?.end) !== action.payload?.end)
      ) {
        const resultCondition = get(state.resultConditions, tabId, {});
        if (tabId && !isEmpty(resultCondition)) {
          state.resultConditions = {
            ...state.resultConditions,
            [tabId]: { ...resultCondition, timeChangeCondition: getTime(new Date()) }
          };
        }
      }
      state.tabConditions[tabId] = action.payload;
    },

    setOpenBookingCarResult: (state, action) => {
      state.openBookingCarResult = action.payload;
    },
    toggleOpenBookingCarResult: (state) => {
      state.openBookingCarResult = !state.openBookingCarResult;
    },
    toggleCollapseCondition: (state) => {
      state.collapseCondition = !state.collapseCondition;
    },
    setSettings: (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    toggleOwnedSelected: (state, action) => {
      if (state.ownedSelected.includes(action.payload)) {
        // Nếu đã tồn tại, xóa phần tử
        state.ownedSelected = state.ownedSelected.filter((item) => item !== action.payload);
      } else {
        // Nếu chưa tồn tại, thêm phần tử
        state.ownedSelected = [...state.ownedSelected, action.payload];
      }
    },
    unsetAssignedCar: (state, action) => {
      const { conditionId, carId, ownedId } = action.payload;

      // Lấy đối tượng result tương ứng với conditionId
      const result = get(state.resultConditions, `${conditionId}.result`);
      if (!isEmpty(result)) {
        // Tìm index của ownedId trong result (giả sử result là một mảng các đối tượng)
        const index = result.findIndex((item) => item.id === ownedId);

        if (index !== -1) {
          const ownedObject = result[index];
          const cars = ownedObject.cars;

          // Loại bỏ car nếu tồn tại trong mảng
          remove(cars, (car) => car.id === carId);

          // Kiểm tra nếu cars rỗng, xóa ownedId khỏi result tại index
          if (isEmpty(cars)) {
            // Nếu cars rỗng, xóa ownedId khỏi result[index] bằng splice
            result.splice(index, 1);
          }
        }
      }
    },
    clearOwnedSelect: (state) => {
      state.ownedSelected = [];
    },
    unSelectOwned: (state, action) => {
      if (state.ownedSelected.includes(action.payload)) {
        // Nếu đã tồn tại, xóa phần tử
        state.ownedSelected = state.ownedSelected.filter((item) => item !== action.payload);
      }
    },
    setSetting: (state, action) => {
      const setting = find(state.settings, { key: action.payload.key });
      if (!isEmpty(setting)) {
        state.settings = {
          ...state.settings,
          [setting.key]: {
            ...setting,
            value: action.payload.value
          }
        };
      }
    },

    setResultConditionType: (state, action) => {
      const tabId = get(state, 'tabSelected.id');

      if (tabId) {
        const resultCondition = get(state.resultConditions, tabId, {});
        state.resultConditions = {
          ...state.resultConditions,
          [tabId]: { ...resultCondition, types: action.payload, timeChangeCondition: getTime(new Date()) }
        };
      }
    },
    setResultConditionTab: (state, action) => {
      const tabId = get(state, 'tabSelected.id');

      if (tabId) {
        const resultCondition = get(state.resultConditions, tabId, {});
        state.resultConditions = {
          ...state.resultConditions,
          [tabId]: { ...resultCondition, tab: action.payload }
        };
      }
    },
    setPushResponseToBookingRequest(state, action) {
      const { requestId, userConfirmId, isAccepted, bookingConditionId } = action.payload;
      state.tabNewNotices = [...new Set([...state.tabNewNotices, bookingConditionId])];

      const tabId = state?.tabSelected?.id; // Truy cập trực tiếp thay vì `get`
      if (!tabId) return;

      const currentRequests = state?.resultRequests?.[tabId]?.data ?? []; // Truy cập trực tiếp

      // Cập nhật requests bằng cách duyệt qua và thay đổi giá trị cần thiết
      const updatedRequests = currentRequests.map((request) => {
        if (request.id === requestId) {
          const key = isAccepted ? 'accepted' : 'denined';
          return {
            ...request,
            [key]: [...(request[key] || []), userConfirmId] // Thêm userConfirmId
          };
        }
        return request;
      });

      // Gán lại dữ liệu cập nhật vào state
      // Gán lại vào state
      set(state, `resultRequests.${tabId}.data`, updatedRequests);
      // state.resultRequests[tabId].data = updatedRequests;
    },

    setPushResponseRemoveFocusToBookingRequest(state) {
      const tabSelectedId = get(state, 'tabSelected.id');

      const currentResultRequest = get(state.resultRequests, tabSelectedId, {});
      state.resultRequests = {
        ...state.resultRequests,
        [tabSelectedId]: { ...currentResultRequest, requestFocusId: null }
      };
    },
    setPushResponseFocusToBookingRequest(state, action) {
      const { tab, bookingFindCarTab, requestId } = action.payload;
      const tabSelectedId = get(state, 'tabSelected.id');
      const bookingConditionId = tab.id;
      const currentRequest = get(state, `resultRequests.${bookingConditionId}`, { isSuccess: false, data: [] });

      const resultCondition = get(state.resultConditions, bookingConditionId, {});
      state.resultConditions = {
        ...state.resultConditions,
        [bookingConditionId]: { ...resultCondition, tab: bookingFindCarTab }
      };

      if (tabSelectedId !== bookingConditionId) {
        state.tabSelected = tab;
      }
      state.resultRequests = {
        ...state.resultRequests,
        [bookingConditionId]: { ...currentRequest, requestFocusId: requestId }
      };

      state.openBookingCarResult = true;
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(bookingConditionApi.endpoints.deleteBookingCondition.matchFulfilled, (state, action) => {
      const tabSelectedId = get(state, 'tabSelected.id');
      const tabDeleted = get(action.payload, 'id');
      if (tabSelectedId === tabDeleted) {
        state.tabSelected = null;
      }
    });
    builder.addMatcher(bookingConditionApi.endpoints.getBookingConditions.matchFulfilled, (state, action) => {
      // const configApprovalList = get(action.payload, configNames.approvalList, {});
      if (!isEmpty(action.payload)) {
        state.tabConditions = keyBy(action.payload, 'id');
      } else {
        state.tabConditions = null;
      }
    });
    builder.addMatcher(bookingConditionApi.endpoints.startSearch.matchFulfilled, (state, action) => {
      // const configApprovalList = get(action.payload, configNames.approvalList, {});
      const tabId = get(state.tabSelected, 'id');

      if (tabId) {
        const resultCondition = get(state.resultConditions, tabId, {
          types: [],
          timeChangeCondition: getTime(new Date())
        });
        state.resultConditions = {
          ...state.resultConditions,
          [tabId]: { ...resultCondition, result: groupByOwned(action.payload), timeGetResult: getTime(new Date()) }
        };
      }
    });

    builder.addMatcher(bookingRequestApi.endpoints.getBookingRequestByCondition.matchFulfilled, (state, action) => {
      const conditionId = get(action.meta.arg.originalArgs, 'conditionId');
      const tabId = get(state.tabSelected, 'id');
      const requestFocusId = get(state.resultRequests, `${tabId}.requestFocusId`, false);
      state.resultRequests = {
        ...state.resultRequests,
        [conditionId]: { isSuccess: true, requestFocusId: requestFocusId, data: action.payload }
      };
    });

    // builder.addMatcher(bookingConditionApi.endpoints.updateWithoutEffectBookingCondition.matchFulfilled, (state, action) => {
    //   // const configApprovalList = get(action.payload, configNames.approvalList, {});
    //   const tabId = get(state.tabSelected, 'id');
    //   const resultCondition = get(state.resultConditions, tabId, {});
    //   if (tabId && !isEmpty(resultCondition)) {
    //     state.resultConditions = {
    //       ...state.resultConditions,
    //       [tabId]: { ...resultCondition, timeChangeCondition: getTime(new Date()) }
    //     };
    //   }
    // });
    builder.addMatcher(settingApi.endpoints.getConfigSettings.matchFulfilled, (state, action) => {
      let settings = state.settings;
      map(configSettings, (defaultSetting, key) => {
        settings = {
          ...settings,
          [key]: {
            ...defaultSetting,
            value: get(action.payload, defaultSetting.key, defaultSetting.value)
          }
        };
      });
      state.settings = settings;
    });
  }
});

// Action creators are generated for each case reducer function
export const {
  resetState,
  setMapDestinations,
  setResultRouteMap,
  setResultConditionType,
  setResultConditionTab,
  changeDirections,
  setTabSelected,
  updateTabCondition,
  updateTabSelectedCondition,
  setOpenBookingCarResult,
  toggleOpenBookingCarResult,
  toggleCollapseCondition,
  setSettings,
  setSetting,
  setPushResponseToBookingRequest,
  setPushResponseFocusToBookingRequest,
  setPushResponseRemoveFocusToBookingRequest,
  toggleOwnedSelected,
  clearOwnedSelect,
  unSelectOwned,
  unsetAssignedCar
} = bookingSlice.actions;

// Selectors
export const selectOwnedSelected = ({ booking }) => booking.ownedSelected;
export const selectMapDestinations = ({ booking }) => booking.map.destinations;
export const selectChangeDirections = ({ booking }) => booking.map.directionsChanged;
export const selectResultRouteMap = ({ booking }) => booking.resultRouteMap;
export const selectResultConditions = ({ booking }) => booking.resultConditions;
// export const selectResultConditionsList = ({ booking }) => get(booking.resultConditions, 'list');
export const selectTabConditions = ({ booking }) => booking.tabConditions;
export const selectTabSelected = ({ booking }) => booking.tabSelected;
export const selectCollapseCondition = ({ booking }) => booking.collapseCondition;
export const selectSettings = ({ booking }) => booking.settings;
export const selectIsOpenBookingCarResult = ({ booking }) => booking.openBookingCarResult;
export const selectTabNewNotices = ({ booking }) => booking.tabNewNotices;

export const selectIsOwnedSelected = createSelector([selectOwnedSelected, (_state, ownedId) => ownedId], (ownedSelected, ownedId) =>
  includes(ownedSelected, ownedId, false)
);

export const selectTabNewNotice = createSelector([selectTabNewNotices, (_state, tabId) => tabId], (tabNewNotices, tabId) =>
  includes(tabNewNotices, tabId, false)
);

export const selectSetting = createSelector([selectSettings, (_state, key) => key], (settings, key) => get(settings, key));
export const selectMultiSetting = createSelector([selectSettings, (_state, keys) => keys], (settings, keys) => {
  let list = {};
  map(keys, ({ key, init }) => {
    list[key] = get(settings, key, init);
  });

  return list;
});

export const selectBookingResultRequests = ({ booking }) => booking.resultRequests;
export const selectBookingConditionResultRequests = createSelector(
  [selectBookingResultRequests, selectTabSelected],
  (resultRequests, tabSelected) => {
    const tabId = get(tabSelected, 'id');
    return get(resultRequests, tabId, { isSuccess: false, data: [] });
  }
);

export const selectBookingConditionResultRequestIsFocus = createSelector(
  [selectBookingResultRequests, selectTabSelected, (_state, requestId) => requestId],
  (resultRequests, tabSelected, requestId) => {
    const tabId = get(tabSelected, 'id');
    const requestFocusId = get(resultRequests, `${tabId}.requestFocusId`);
    return requestFocusId === requestId;
  }
);

export const selectTabSelectedCondition = createSelector([selectTabConditions, selectTabSelected], (conditions, tabSelected) => {
  if (isEmpty(conditions) || isEmpty(tabSelected)) {
    return null;
  }

  return conditions[tabSelected.id] || null;
});

export const selectTabSelectedConditionTime = createSelector([selectTabSelectedCondition], (condition) => {
  if (isEmpty(condition)) {
    return null;
  }

  return {
    start: condition.start,
    end: condition.end
  };
});

export const selectTabCondition = createSelector([selectTabConditions, (_state, tabId) => tabId], (conditions, tabId) => {
  let condition = null;
  if (!isEmpty(conditions)) {
    condition = find(conditions, { id: tabId }, null);
  }
  return condition;
});

export const selectResultCondition = createSelector([selectResultConditions, (_state, tabId) => tabId], (resultConditions, tabId) =>
  get(resultConditions, tabId, null)
);

export const selectResultConditionType = createSelector([selectResultConditions, selectTabSelected], (resultConditions, tabSelected) => {
  const tabId = get(tabSelected, 'id');
  return get(resultConditions, `${tabId}.types`, []);
});

export const selectResultConditionTab = createSelector([selectResultConditions, selectTabSelected], (resultConditions, tabSelected) => {
  const tabId = get(tabSelected, 'id');
  return get(resultConditions, `${tabId}.tab`, bookingFindCarTabs.carFind.value);
});

export const selectResultConditionList = createSelector([selectResultConditions, selectTabSelected], (resultConditions, tabSelected) => {
  const tabId = get(tabSelected, 'id');
  return get(resultConditions, `${tabId}.result`, null);
});

export const selectResultConditionListSendTo = createSelector(
  [selectOwnedSelected, selectResultConditions, selectTabSelected],
  (sendTo, resultConditions, tabSelected) => {
    const tabId = get(tabSelected, 'id');
    const conditions = get(resultConditions, `${tabId}.result`, []);

    // Lọc conditions có id thuộc mảng sendTo
    const filteredConditions = Array.isArray(conditions) ? conditions.filter((condition) => sendTo.includes(condition.id)) : [];

    return filteredConditions;
  }
);

export const selectResultConditionTime = createSelector([selectResultConditions, selectTabSelected], (resultConditions, tabSelected) => {
  const tabId = get(tabSelected, 'id');
  const resultCondition = get(resultConditions, tabId);
  if (resultCondition) {
    return {
      timeGetResult: resultCondition.timeGetResult,
      timeChangeCondition: resultCondition.timeChangeCondition
    };
  }
  return {
    timeGetResult: null,
    timeChangeCondition: null
  };
});

// Reducer
export default bookingSlice.reducer;
